/**
 * Lists service — a list's rows. Reads return entries (see
 * collections/entries.ts): its contents, members or matches, or what an
 * unsaved filter would match; each is its own query against the source that
 * fits it, and every one takes the same `fields` option. Writes: the listing
 * verbs (`add`, `update`, `remove`, `move`, `reorder`, … — see
 * services/listings.ts) write `record_listings` rows by id, and `keepMatches`
 * lists the filter's matches. An entry — its listing and its record together —
 * is written through the list handle (`createListHandle`, `auger.list(id)`),
 * the one write path for a record in a list.
 * `createRecordEntryReads` is the inverse, a record's lists, which `createAuger`
 * puts under `records`.
 */

import {
  type Entry,
  entryFromEntriesRow,
  entryFromListing,
  entryFromMatch,
  type EntryReadOptions,
  type EntryRecord,
  entrySelection,
} from "../collections/entries.js"
import type { SavedFilter } from "../fields/filter.js"
import {
  fetchEntryListings,
  fetchFilterPreviewEntries,
  fetchListContents,
  fetchListCounts,
  fetchListMatchEntries,
  fetchListMemberEntries,
  fetchListRecordIds,
  fetchRecordEntries,
  fetchRecordListingEntries,
  fetchRecordMatchingEntries,
} from "../gql/lists.js"
import type { Mutate, Query } from "../index.js"
import { createListingsService, type ListingChanges, type ListingItem } from "./listings.js"
import { type createRecordsService, RelationFieldInBatchError } from "./records.js"

/** A list's rows that a saved filter adds, and why the filter can't run (null
 *  when it can). */
export interface FilteredEntries {
  entries: Entry[]
  filterError: string | null
}

/** An entry handed to another list's handle. */
export class EntryListMismatchError extends Error {
  constructor(
    readonly listId: number,
    readonly entryListId: number
  ) {
    super(`This entry is in list #${entryListId}, not list #${listId}.`)
    this.name = "EntryListMismatchError"
  }
}

/** A list-side read's variables: what each row carries, which rows, and which
 *  page of them. */
const listVariables = (options: EntryReadOptions) => ({
  ...entrySelection(options),
  recordWhere: options.where ?? {},
  limit: options.limit ?? null,
  offset: options.offset ?? null,
})

/** How many rows a list has: listed by hand, matched by its saved filter, and
 *  in all (one per record, whichever way it's in). */
export interface ListCounts {
  members: number
  matches: number
  contents: number
}

export function createListsService({
  query,
  mutate,
  records,
}: {
  query: Query
  mutate: Mutate
  records: Pick<ReturnType<typeof createRecordsService>, "create" | "get" | "update" | "updateMany">
}) {
  const listings = createListingsService({ query, mutate, records })

  /** Members and matches together, one entry per record: listed ones in their
   *  position order (newest first among equals), then the rest by name. Null
   *  when the list isn't visible. */
  async function contents(
    listId: number,
    options: EntryReadOptions = {}
  ): Promise<FilteredEntries | null> {
    const data = await query(fetchListContents, {
      listId,
      ...listVariables(options),
    })
    if (!data.list) return null
    return {
      entries: (data.list.contents ?? []).flatMap((row) =>
        row.record ? [entryFromEntriesRow(row, row.record)] : []
      ),
      filterError: data.list.filter_error ?? null,
    }
  }

  /** The records listed by hand, in their position order. */
  async function members(listId: number, options: EntryReadOptions = {}): Promise<Entry[]> {
    const data = await query(fetchListMemberEntries, {
      listId,
      ...listVariables(options),
    })
    return data.listings.map(({ record, ...listing }) => entryFromListing(listing, record))
  }

  /** The records the list's saved filter matches, by name, listed or not. Null
   *  when the list isn't visible. */
  async function matches(
    listId: number,
    options: EntryReadOptions = {}
  ): Promise<FilteredEntries | null> {
    const data = await query(fetchListMatchEntries, {
      listId,
      ...listVariables(options),
    })
    if (!data.list) return null
    return {
      entries: (data.list.matches ?? []).flatMap((m) =>
        m.record ? [entryFromMatch(listId, m.record.id, m.record)] : []
      ),
      filterError: data.list.filter_error ?? null,
    }
  }

  /** The records an unsaved filter on `recordType` matches, as the list's
   *  entries would be: each with its listing when it's already listed. Throws
   *  when the filter can't run. */
  async function preview(
    listId: number,
    recordType: string,
    filter: SavedFilter,
    options: EntryReadOptions = {}
  ): Promise<Entry[]> {
    const data = await query(fetchFilterPreviewEntries, {
      listId,
      recordType,
      filter,
      ...listVariables({ recordType, ...options }),
    })
    return data.records.map(({ listings, ...record }) => {
      const listing = listings.at(0) ?? null
      return {
        ...entryFromMatch(listId, record.id, record),
        listed: listing != null,
        listing,
      }
    })
  }

  /** How many rows each list has, by list id, without reading them; a list it
   *  can't see is missing. */
  async function counts(listIds: number[]): Promise<Map<number, ListCounts>> {
    const byList = new Map<number, ListCounts>()
    if (!listIds.length) return byList
    const data = await query(fetchListCounts, { listIds })
    for (const list of data.lists) {
      byList.set(list.id, {
        members: list.members_aggregate.aggregate?.count ?? 0,
        matches: list.matches_aggregate.aggregate?.count ?? 0,
        contents: list.contents_aggregate.aggregate?.count ?? 0,
      })
    }
    return byList
  }

  /** The ids of the records in each list's contents (members and matches),
   *  by list id; a list it can't see has none. */
  async function recordIds(listIds: number[]): Promise<Map<number, number[]>> {
    const byList = new Map<number, number[]>(listIds.map((id) => [id, []]))
    if (!listIds.length) return byList
    const data = await query(fetchListRecordIds, { listIds })
    for (const list of data.lists) {
      byList.set(
        list.id,
        list.contents.flatMap((c) => (c.record_id == null ? [] : [c.record_id]))
      )
    }
    return byList
  }

  /** List every record the list's saved filter matches that isn't listed yet,
   *  appended in name order. */
  async function keepMatches(listId: number) {
    const found = await matches(listId, { fields: [] })
    const recordIds = (found?.entries ?? []).map((e) => e.recordId)
    return listings.addMany(listId, recordIds)
  }

  return {
    ...listings,
    contents,
    members,
    matches,
    preview,
    counts,
    recordIds,
    keepMatches,
  }
}

/** A record's lists, as entries whose record is the list. */
export function createRecordEntryReads({ query }: { query: Query }) {
  const listOptions = (options: EntryReadOptions) =>
    entrySelection({ recordType: "list", ...options })

  /** The lists a record is in, listed or matched. */
  async function entries(
    recordId: number,
    options: EntryReadOptions = {}
  ): Promise<Entry<EntryRecord>[]> {
    const data = await query(fetchRecordEntries, {
      recordId,
      ...listOptions(options),
    })
    return (data.record?.entries ?? []).flatMap((row) =>
      row.list ? [entryFromEntriesRow(row, row.list)] : []
    )
  }

  /** The lists a record is listed in by hand, newest first. */
  async function listings(
    recordId: number,
    options: EntryReadOptions = {}
  ): Promise<Entry<EntryRecord>[]> {
    const data = await query(fetchRecordListingEntries, {
      recordId,
      ...listOptions(options),
    })
    return data.listings.map(({ list, ...listing }) => entryFromListing(listing, list))
  }

  /** The lists whose saved filter matches a record. */
  async function matchings(
    recordId: number,
    options: EntryReadOptions = {}
  ): Promise<Entry<EntryRecord>[]> {
    const data = await query(fetchRecordMatchingEntries, {
      recordId,
      ...listOptions(options),
    })
    return (data.record?.matchings ?? []).flatMap((row) =>
      row.list ? [entryFromMatch(Number(row.list_id), recordId, row.list)] : []
    )
  }

  return { entries, listings, matchings }
}

// LIST HANDLE =================================================================

const hasKeys = (o: object | undefined): o is object => !!o && Object.keys(o).length > 0

/**
 * `auger.list(id)` — the one write path for a record in a list.
 *
 * `update` takes a batch of `{ entry, listing, record }`: the entry (the one a
 * caller read, or `{ listId, recordId }`) and its changes. A single edit is a
 * batch of one; a fill repeats the same changes; a paste gives each its own.
 * Record parts go through the planner (`records.update`), in one
 * `records.updateMany` when every one is the same. Listing parts go to the
 * entries' `record_listings` rows in one batch; an entry with no listing is
 * reported in `unlisted`, unless `create` lists it. A refused record part
 * skips its entry's listing part. Every outcome is reported per entry; only an
 * entry from another list throws (`EntryListMismatchError`), before anything
 * is written.
 *
 * `archive` / `unarchive` archive the entries' records.
 */
export function createListHandle(
  listId: number,
  {
    query,
    lists,
    records,
  }: {
    query: Query
    lists: ReturnType<typeof createListsService>
    records: Pick<
      ReturnType<typeof createRecordsService>,
      "update" | "updateMany" | "archiveMany" | "unarchiveMany"
    >
  }
) {
  type EntryArg = Pick<Entry<unknown>, "listId" | "recordId"> &
    Partial<Pick<Entry<unknown>, "listing">>

  const assertOwn = (entry: EntryArg) => {
    if (entry.listId !== listId) throw new EntryListMismatchError(listId, entry.listId)
    if (entry.listing && entry.listing.list_id !== listId)
      throw new EntryListMismatchError(listId, entry.listing.list_id)
  }

  async function update(
    writes: Array<{ entry: EntryArg; listing?: ListingChanges; record?: Record<string, unknown> }>,
    { create = false }: { create?: boolean } = {}
  ) {
    writes.forEach((w) => assertOwn(w.entry))
    const rejected: Array<{ recordId: number; error: string }> = []
    const missing: number[] = []
    const unlisted: number[] = []
    const written = new Map<
      number,
      { recordId: number; listing: ListingItem | null; created: boolean }
    >()
    const touch = (recordId: number) => {
      const row = written.get(recordId) ?? { recordId, listing: null, created: false }
      written.set(recordId, row)
      return row
    }
    const refused = new Set<number>()
    const refuse = (recordId: number, err: unknown) => {
      refused.add(recordId)
      rejected.push({ recordId, error: err instanceof Error ? err.message : String(err) })
    }

    // Record parts: one write when they're all the same, else one per entry.
    const recordWrites = writes.filter((w) => hasKeys(w.record))
    const same =
      recordWrites.length > 1 &&
      recordWrites.every(
        (w) => JSON.stringify(w.record) === JSON.stringify(recordWrites[0]!.record)
      )
    let perRecord = recordWrites
    if (same) {
      const ids = [...new Set(recordWrites.map((w) => w.entry.recordId))]
      try {
        const result = await records.updateMany(ids, recordWrites[0]!.record!)
        missing.push(...result.missingIds)
        result.missingIds.forEach((id) => refused.add(id))
        ids.filter((id) => !result.missingIds.includes(id)).forEach(touch)
        perRecord = []
      } catch (err) {
        // A relation field is rows per record: write each through the planner.
        if (!(err instanceof RelationFieldInBatchError)) {
          ids.forEach((id) => refuse(id, err))
          perRecord = []
        }
      }
    }
    for (const w of perRecord) {
      try {
        await records.update(w.entry.recordId, w.record!, { read: false })
        touch(w.entry.recordId)
      } catch (err) {
        refuse(w.entry.recordId, err)
      }
    }

    // Listing parts, for the entries whose record part (if any) was written.
    const listingWrites = writes.filter((w) => hasKeys(w.listing) && !refused.has(w.entry.recordId))
    if (listingWrites.length) {
      // The listings entries don't carry: found in one read.
      const unknown = listingWrites.filter((w) => !w.entry.listing).map((w) => w.entry.recordId)
      const found = new Map<number, number>()
      if (unknown.length) {
        const data = await query(fetchEntryListings, { listId, recordIds: unknown })
        for (const l of data.listings) found.set(l.record_id, l.id)
      }
      const listingIdOf = (w: (typeof listingWrites)[number]) =>
        w.entry.listing?.id ?? found.get(w.entry.recordId)

      // Matches: listed when asked (one insert), else reported.
      const unlistedWrites = listingWrites.filter((w) => listingIdOf(w) == null)
      if (create && unlistedWrites.length) {
        const { added } = await lists.addMany(
          listId,
          unlistedWrites.map((w) => w.entry.recordId)
        )
        for (const l of added) {
          found.set(l.record_id, l.id)
          touch(l.record_id).created = true
        }
      } else {
        unlisted.push(...unlistedWrites.map((w) => w.entry.recordId))
      }

      const recordOf = new Map<number, number>()
      const updates: Array<{ id: number; changes: ListingChanges }> = []
      for (const w of listingWrites) {
        const id = listingIdOf(w)
        if (id == null) continue
        recordOf.set(id, w.entry.recordId)
        updates.push({ id, changes: w.listing! })
      }
      if (updates.length) {
        const result = await lists.updateMany(updates)
        for (const l of result.updated) touch(l.record_id).listing = l
        for (const id of result.missingIds) missing.push(recordOf.get(id)!)
        for (const r of result.rejected) {
          rejected.push({ recordId: recordOf.get(r.id)!, error: r.error })
        }
      }
    }

    return { updated: [...written.values()], unlisted, rejected, missing }
  }

  /** Archive the records of these entries, listed or matched. */
  function archive(entries: EntryArg[]) {
    entries.forEach(assertOwn)
    return records.archiveMany([...new Set(entries.map((e) => e.recordId))])
  }

  /** Unarchive the records of these entries, listed or matched. */
  function unarchive(entries: EntryArg[]) {
    entries.forEach(assertOwn)
    return records.unarchiveMany([...new Set(entries.map((e) => e.recordId))])
  }

  return { update, archive, unarchive }
}
